import { useState, useEffect } from "react";

/**
 * Hook to detect media query matches
 * @param query - CSS media query string (e.g., '(max-width: 639px)')
 * @returns boolean indicating if the query matches
 */
export const useMediaQuery = (query: string): boolean => {
	const [matches, setMatches] = useState(() => {
		if (typeof window !== "undefined") {
			return window.matchMedia(query).matches;
		}
		return false;
	});

	useEffect(() => {
		if (typeof window === "undefined") return;

		const mediaQuery = window.matchMedia(query);
		setMatches(mediaQuery.matches);

		const handler = (event: MediaQueryListEvent) => {
			setMatches(event.matches);
		};

		mediaQuery.addEventListener("change", handler);
		return () => mediaQuery.removeEventListener("change", handler);
	}, [query]);

	return matches;
};

/**
 * Tailwind breakpoints:
 * - sm: 640px
 * - md: 768px
 * - lg: 1024px
 * - xl: 1280px
 * - 2xl: 1536px
 */

/**
 * Check if viewport is mobile (< 640px)
 */
export const useIsMobile = (): boolean => useMediaQuery("(max-width: 639px)");

/**
 * Check if viewport is tablet (640px - 1023px)
 */
export const useIsTablet = (): boolean =>
	useMediaQuery("(min-width: 640px) and (max-width: 1023px)");

/**
 * Check if viewport is desktop (>= 1024px)
 */
export const useIsDesktop = (): boolean => useMediaQuery("(min-width: 1024px)");

/**
 * Check if viewport is small or larger (>= 640px)
 */
export const useIsSmUp = (): boolean => useMediaQuery("(min-width: 640px)");

/**
 * Check if viewport is medium or larger (>= 768px)
 */
export const useIsMdUp = (): boolean => useMediaQuery("(min-width: 768px)");

/**
 * Check if viewport is large or larger (>= 1024px)
 */
export const useIsLgUp = (): boolean => useMediaQuery("(min-width: 1024px)");
